import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categoriesAPI } from '../lib/api';
import { getCategoryImage } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, ArrowRight, Star, Shield, Clock, CheckCircle } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [categories, setCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesAPI.getAll();
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  const featuredCategories = ['batiment', 'reparation', 'services', 'nature', 'artisanat', 'creatif'];

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative h-[85vh] min-h-[600px] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/5493672/pexels-photo-5493672.jpeg"
            alt="Professional craftsman"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-gradient"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-sm mb-6">
              <span>📍</span>
              <span>Gatineau • Outaouais • Québec</span>
            </div>
            <h1 className="text-5xl md:text-6xl text-white mb-6 leading-[1.1]" style={{ fontFamily: '"DM Serif Display", serif' }}>
              {t('heroTitle')}
            </h1>
            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              {t('heroSubtitle')}
            </p>
            
            {/* Search Form */}
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-14 pl-12 pr-4 rounded-xl text-base bg-white border-0 shadow-lg"
                  data-testid="hero-search-input"
                />
              </div>
              <Button
                type="submit"
                className="h-14 px-8 bg-orange-600 hover:bg-orange-700 text-white rounded-xl shadow-lg shadow-orange-600/20 btn-press"
                data-testid="hero-search-btn"
              >
                {t('search')}
              </Button>
            </form>

            {/* Popular searches */}
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="text-white/60 text-sm">{t('popular')} :</span>
              {['Plombier', 'Électricien', 'Jardinier', 'Peintre'].map((term) => (
                <Link
                  key={term}
                  to={`/search?q=${term}`}
                  className="text-white/80 hover:text-white text-sm underline underline-offset-2 transition-colors"
                >
                  {term}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{t('verified')}</p>
                <p className="text-sm text-slate-500">{t('verifiedDesc')}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center">
                <Star className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">4.8/5</p>
                <p className="text-sm text-slate-500">{t('avgRating')}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{t('guarantee')}</p>
                <p className="text-sm text-slate-500">{t('guaranteeDesc')}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{t('fast')}</p>
                <p className="text-sm text-slate-500">{t('fastDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 md:py-32" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-12">
            <div>
              <p className="text-sm text-orange-600 font-medium tracking-wide uppercase mb-2">{t('ourServices')}</p>
              <h2 className="text-4xl md:text-5xl text-slate-900" style={{ fontFamily: '"DM Serif Display", serif' }}>
                {t('exploreCategories')}
              </h2>
            </div>
            <Link to="/categories">
              <Button variant="outline" className="rounded-full border-2 border-slate-200 hover:bg-slate-50 gap-2" data-testid="view-all-categories">
                {t('viewAll')}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-72 rounded-xl skeleton-loading"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredCategories.map((key, index) => {
                const category = categories[key];
                if (!category) return null;
                
                return (
                  <Link
                    key={key}
                    to={`/search?category=${key}`}
                    className={`group relative overflow-hidden rounded-xl card-hover animate-fade-in stagger-${index + 1}`}
                    style={{ animationFillMode: 'backwards' }}
                    data-testid={`category-card-${key}`}
                  >
                    <div className="aspect-[4/3] relative">
                      <img
                        src={getCategoryImage(key)}
                        alt={category.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h3 className="text-xl text-white mb-1" style={{ fontFamily: '"DM Serif Display", serif' }}>
                          {category.name.replace('Métiers ', '')}
                        </h3>
                        <p className="text-white/70 text-sm">
                          {category.trades.length} {t('tradesAvailable')}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-32 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm text-orange-600 font-medium tracking-wide uppercase mb-2">{t('simpleAndFast')}</p>
            <h2 className="text-4xl md:text-5xl text-slate-900" style={{ fontFamily: '"DM Serif Display", serif' }}>
              {t('howItWorks')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { step: '01', title: t('step1Title'), description: t('step1Desc') },
              { step: '02', title: t('step2Title'), description: t('step2Desc') },
              { step: '03', title: t('step3Title'), description: t('step3Desc') },
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-light text-slate-100 mb-4" style={{ fontFamily: '"DM Serif Display", serif' }}>
                  {item.step}
                </div>
                <h3 className="text-2xl text-slate-900 mb-3" style={{ fontFamily: '"DM Serif Display", serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-900" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl text-white mb-6" style={{ fontFamily: '"DM Serif Display", serif' }}>
            {t('youArePro')}
          </h2>
          <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
            {t('joinNetwork')}
          </p>
          <Link to="/register">
            <Button className="h-14 px-8 bg-orange-600 hover:bg-orange-700 text-white rounded-full text-lg btn-press shadow-lg shadow-orange-600/30" data-testid="cta-register-btn">
              {t('createProProfile')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
